/* mto-list.js — MTO Brand List slider */
(() => {
  if (customElements.get('mto-list-slider')) return;

  class MtoListSlider extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.sliderDesktop = this.dataset.sliderDesktop === 'true';
      this.sliderMobile  = this.dataset.sliderMobile  === 'true';
      this.autoplay      = this.dataset.autoplay       === 'true';
      this.autoplaySpeed = Math.max(1000, parseInt(this.dataset.autoplaySpeed, 10) || 4000);
      this.pauseHover    = this.dataset.pauseHover     === 'true';
      this.dotsDesktop   = this.dataset.dotsDesktop    === 'true';
      this.dotsMobile    = this.dataset.dotsMobile     === 'true';

      this._section  = this.closest('.mto-list');
      this._track    = this.querySelector('.mto-list__track');
      this._prevBtn  = this.querySelector('.mto-list__arrow--prev');
      this._nextBtn  = this.querySelector('.mto-list__arrow--next');
      this._dotsEl   = this.querySelector('.mto-list__dots');
      this._dots     = [];

      this._timer      = null;
      this._wasMobile  = null;

      this._handleResize = this._handleResize.bind(this);

      this._ro = new ResizeObserver(this._handleResize);
      this._ro.observe(this);

      this._init();
      this._bindEvents();
    }

    get _isMobile() {
      return window.innerWidth < 750;
    }

    get _sliderActive() {
      return this._isMobile ? this.sliderMobile : this.sliderDesktop;
    }

    _init() {
      const active = this._sliderActive;

      if (this._section) {
        this._section.classList.toggle('mto-list--slider', active);
      }

      if (this._prevBtn) this._prevBtn.hidden = !active;
      if (this._nextBtn) this._nextBtn.hidden = !active;

      const showDots = active && (this._isMobile ? this.dotsMobile : this.dotsDesktop);
      if (this._dotsEl) {
        this._dotsEl.hidden = !showDots;
        if (showDots) {
          this._buildDots();
        } else {
          this._clearDots();
        }
      }

      if (active) {
        if (this.autoplay) this._startAutoplay();
      } else {
        this._stopAutoplay();
        if (this._track) this._track.scrollLeft = 0;
      }
    }

    _handleResize() {
      const mobile = this._isMobile;
      if (mobile === this._wasMobile) return;
      this._wasMobile = mobile;
      this._stopAutoplay();
      this._init();
    }

    _bindEvents() {
      const sig = { signal: (this._abort = new AbortController()).signal };

      if (this._prevBtn) {
        this._prevBtn.addEventListener('click', () => this._step(-1), sig);
      }
      if (this._nextBtn) {
        this._nextBtn.addEventListener('click', () => this._step(1), sig);
      }

      if (this.pauseHover && this._track) {
        this._track.addEventListener('mouseenter', () => this._stopAutoplay(), sig);
        this._track.addEventListener('mouseleave', () => {
          if (this._sliderActive && this.autoplay) this._startAutoplay();
        }, sig);
      }

      if (this._track) {
        this._track.addEventListener('scroll', () => this._updateActiveDot(), { passive: true, signal: this._abort.signal });
      }
    }

    _buildDots() {
      if (!this._dotsEl || !this._track) return;
      const items = this._track.querySelectorAll('.mto-list__item');
      this._dotsEl.innerHTML = '';
      this._dots = [];
      items.forEach((_, i) => {
        const btn = document.createElement('button');
        btn.className = 'mto-list__dot' + (i === 0 ? ' mto-list__dot--active' : '');
        btn.type = 'button';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-label', `Item ${i + 1}`);
        btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        btn.addEventListener('click', () => this._scrollToIndex(i));
        this._dotsEl.appendChild(btn);
        this._dots.push(btn);
      });
    }

    _clearDots() {
      if (this._dotsEl) this._dotsEl.innerHTML = '';
      this._dots = [];
    }

    _scrollToIndex(index) {
      if (!this._track) return;
      const items = this._track.querySelectorAll('.mto-list__item');
      const item = items[index];
      if (!item) return;
      this._track.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
    }

    _updateActiveDot() {
      if (!this._dots.length || !this._track) return;
      const { scrollLeft, clientWidth } = this._track;
      const center = scrollLeft + clientWidth / 2;
      const items = Array.from(this._track.querySelectorAll('.mto-list__item'));
      let activeIndex = 0;
      let minDist = Infinity;
      items.forEach((item, i) => {
        const dist = Math.abs((item.offsetLeft + item.offsetWidth / 2) - center);
        if (dist < minDist) { minDist = dist; activeIndex = i; }
      });
      this._dots.forEach((dot, i) => {
        const isActive = i === activeIndex;
        dot.classList.toggle('mto-list__dot--active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    _step(dir) {
      if (!this._track) return;
      const item = this._track.querySelector('.mto-list__item');
      if (!item) return;
      const style   = getComputedStyle(this._track);
      const gap     = parseFloat(style.gap || style.columnGap) || 0;
      const itemW   = item.offsetWidth + gap;
      this._track.scrollBy({ left: dir * itemW, behavior: 'smooth' });
    }

    _startAutoplay() {
      this._stopAutoplay();
      this._timer = setInterval(() => {
        if (!this._sliderActive || !this._track) return;
        const { scrollLeft, scrollWidth, clientWidth } = this._track;
        if (scrollLeft + clientWidth >= scrollWidth - 2) {
          this._track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this._step(1);
        }
      }, this.autoplaySpeed);
    }

    _stopAutoplay() {
      clearInterval(this._timer);
      this._timer = null;
    }

    disconnectedCallback() {
      this._stopAutoplay();
      if (this._ro)    this._ro.disconnect();
      if (this._abort) this._abort.abort();
    }
  }

  customElements.define('mto-list-slider', MtoListSlider);
})();
